import AxiosInstance from "../config/AxiosInstance";

/**
 * Back-office (dashboard) API client for the three reservation resources:
 *   - reservation_settings  (one row per company+location, hard-deleted)
 *   - reservation_operating_hours  (many per company+location+dayOfWeek, hard-deleted)
 *   - reservation_block_periods  (soft-deleted, isDeleted flag)
 *
 * Endpoints are mounted in dev-core under `/api/reservation-*` and are
 * scoped server-side to `req.user.companyId` (JWT). The shared `AxiosInstance`
 * already injects the bearer token.
 */

// ---------- Reservation Settings ----------

export interface ReservationSettingsPayload {
  locationId: string;
  reservationsEnabled?: boolean;
  maxPartySize?: number | null;
  defaultDurationMinutes?: number;
  bookingWindowDays?: number;
  reservationCapacity?: number | null;
  reservationsPaused?: boolean;
  /** Reply-To of the guest confirmation / cancellation emails. null = none. */
  contactEmail?: string | null;
  /** Venue wording shown as "A note from us" in the confirmation email. null = hidden. */
  policy?: string | null;
  /** Per-location kill switch for ALL guest reservation emails. */
  emailsEnabled?: boolean;
}

export const createReservationSettings = async (
  payload: ReservationSettingsPayload
) => {
  try {
    const { data } = await AxiosInstance.post("/api/reservation-settings", payload);
    return data;
  } catch (error) {
    throw error;
  }
};

export const getReservationSettingsList = async (
  page: number = 1,
  limit: number = 50,
  locationId?: string
) => {
  try {
    const { data } = await AxiosInstance.get("/api/reservation-settings", {
      params: { page, limit, locationId },
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const getReservationSettingsById = async (id: string) => {
  try {
    const { data } = await AxiosInstance.get(`/api/reservation-settings/${id}`);
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateReservationSettings = async (
  id: string,
  payload: Partial<ReservationSettingsPayload>
) => {
  try {
    const { data } = await AxiosInstance.put(`/api/reservation-settings/${id}`, payload);
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteReservationSettings = async (id: string) => {
  try {
    const { data } = await AxiosInstance.delete(`/api/reservation-settings/${id}`);
    return data;
  } catch (error) {
    throw error;
  }
};

// ---------- Reservation Operating Hours ----------

export interface ReservationOperatingHoursPayload {
  locationId: string;
  /** 0 = Sunday … 6 = Saturday, matching JS Date.getDay(). */
  dayOfWeek: number;
  /** "HH:mm" or "HH:mm:ss". */
  openTime: string;
  /** "HH:mm" or "HH:mm:ss". */
  closeTime: string;
}

export const createReservationOperatingHours = async (
  payload: ReservationOperatingHoursPayload
) => {
  try {
    const { data } = await AxiosInstance.post("/api/reservation-operating-hours", payload);
    return data;
  } catch (error) {
    throw error;
  }
};

export const getReservationOperatingHoursList = async (
  page: number = 1,
  limit: number = 50,
  locationId?: string,
  dayOfWeek?: number
) => {
  try {
    const { data } = await AxiosInstance.get("/api/reservation-operating-hours", {
      params: { page, limit, locationId, dayOfWeek },
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateReservationOperatingHours = async (
  id: string,
  payload: Partial<ReservationOperatingHoursPayload>
) => {
  try {
    const { data } = await AxiosInstance.put(`/api/reservation-operating-hours/${id}`, payload);
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteReservationOperatingHours = async (id: string) => {
  try {
    const { data } = await AxiosInstance.delete(`/api/reservation-operating-hours/${id}`);
    return data;
  } catch (error) {
    throw error;
  }
};

// ---------- Reservation Block Periods ----------

export interface ReservationBlockPeriodPayload {
  locationId: string;
  /** ISO-8601 datetime string. */
  blockedFrom: string;
  /** ISO-8601 datetime string. Must be after `blockedFrom`. */
  blockedTo: string;
  reason?: string | null;
}

export const createReservationBlockPeriod = async (
  payload: ReservationBlockPeriodPayload
) => {
  try {
    const { data } = await AxiosInstance.post("/api/reservation-block-periods", payload);
    return data;
  } catch (error) {
    throw error;
  }
};

export const getReservationBlockPeriodsList = async (
  page: number = 1,
  limit: number = 50,
  locationId?: string
) => {
  try {
    const { data } = await AxiosInstance.get("/api/reservation-block-periods", {
      params: { page, limit, locationId },
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const updateReservationBlockPeriod = async (
  id: string,
  payload: Partial<ReservationBlockPeriodPayload>
) => {
  try {
    const { data } = await AxiosInstance.put(`/api/reservation-block-periods/${id}`, payload);
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteReservationBlockPeriod = async (id: string) => {
  try {
    const { data } = await AxiosInstance.delete(`/api/reservation-block-periods/${id}`);
    return data;
  } catch (error) {
    throw error;
  }
};
